import React, { FC } from "react";
import { List, Space } from "antd";
import Title from "antd/lib/typography/Title";
import { useIntl } from "react-intl";
import { Maybe } from "graphql/jsutils/Maybe";
import { Patientportal_PortalFacilityMsg } from "../../src/generated/graphql";
import {
  MailOutlined,
  PhoneOutlined,
  PushpinOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import {
  buildAddressAdditionString,
  buildAddressString,
  buildAddressStringShort,
  extractPrimaryAddress,
  formatPhoneNumber,
} from "../../helpers/utils";

interface FacilityDetailsProps {
  portalFacility: Maybe<Patientportal_PortalFacilityMsg> | undefined;
}

interface PortalFacilityItem {
  id: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

export const FacilityDetails: FC<FacilityDetailsProps> = ({
  portalFacility,
}) => {
  const { formatMessage } = useIntl();

  const contact = portalFacility?.contact;

  const phoneNumbers: string[] =
    [
      ...(contact?.landlineNumbers ?? []),
      ...(contact?.mobileNumbers ?? []),
    ].map((number) => formatPhoneNumber(number)) ?? [];
  const faxNumbers: string[] =
    contact?.faxNumbers?.map((number) => formatPhoneNumber(number)) ?? [];

  const primaryAddress = extractPrimaryAddress(contact);

  const addressString = buildAddressString(primaryAddress);

  const email = (
    contact?.emails?.find((email) => email?.primary) ?? contact?.emails?.[0]
  )?.email;

  const renderNumbers = (numbers: string[]) =>
    !!numbers?.length ? (
      <Space direction={"vertical"} style={{ marginLeft: "12px" }}>
        {numbers.map((number) => (
          <div key={number}>{number}</div>
        ))}
      </Space>
    ) : (
      "-"
    );

  const facilityItems: PortalFacilityItem[] = [
    {
      id: "phone",
      icon: <PhoneOutlined />,
      content: renderNumbers(phoneNumbers),
    },
    {
      id: "fax",
      icon: <PrinterOutlined />,
      content: renderNumbers(faxNumbers),
    },
    {
      id: "e-mail",
      icon: <MailOutlined />,
      content: email || "-",
    },
    {
      id: "address",
      icon: <PushpinOutlined />,
      content: addressString ? (
        <div>
          <div>
            <a
              target={"_blank"}
              rel={"noreferrer"}
              href={`https://maps.google.com/?q=${addressString}`}
            >
              {buildAddressStringShort(primaryAddress)}
            </a>
          </div>
          <div>
            <a
              target={"_blank"}
              rel={"noreferrer"}
              href={`https://maps.google.com/?q=${addressString}`}
            >
              {buildAddressAdditionString(primaryAddress)}
            </a>
          </div>
        </div>
      ) : (
        "-"
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ fontWeight: "bold" }}>
        {formatMessage({ id: "facility-details.contact" })}
      </Title>

      <List
        dataSource={facilityItems}
        renderItem={(item) => (
          <List.Item key={item.id}>
            <List.Item.Meta
              avatar={item.icon}
              title={<b>{formatMessage({ id: item.id })}</b>}
              description={item.content}
            />
          </List.Item>
        )}
      />
    </div>
  );
};
